/**
 * 用途：数据分析页，提供多维度广告效果报表，支持按时间、渠道、账户等条件筛选分析
 * 路由：/analytics
 */
import { useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { MultiSelect } from '../components/MultiSelect';
import Pagination from '../components/Pagination';
import { useAppStore } from '../store/useAppStore';
import { sf } from '../lib/fieldPolicy';
import { showAlert } from '../store/useAlertStore';

interface ProfitRow {
  site: string;
  revenue: string;
  adSpend: string;
  refund: string;
  refundRate: string;
  shipping: string;
  profit: string;
  margin: string;
  trend: 'up' | 'down';
  trendVal: string;
}

interface SkuRow {
  sku: string;
  name: string;
  spend: string;
  orders: number;
  revenue: string;
  roas: string;
  profit: string;
  stock: string;
  suggestion: string;
  suggestionColor: string;
}

export default function Analytics() {
  const isMobile = useIsMobile();
  const { adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  // L2 字段（Optimizer+ 可见，Viewer 脱敏）
  const sfRevenue  = (v: string) => sf(v, 'revenue',     userLevel).value ?? '$*';
  const sfSpend    = (v: string) => sf(v, 'spend',       userLevel).value ?? '$*';
  // L3 字段 alwaysLock（Lead+ 可见，Optimizer 也锁定）
  const sfProfit   = (v: string) => sf(v, 'profit',      userLevel).value ?? '🔒';
  const sfMargin   = (v: string) => sf(v, 'margin',      userLevel).value ?? '🔒';
  // L3 字段（Lead+ 明文，Optimizer 脱敏，Viewer 锁定）
  const sfMonthly  = (v: string) => sf(v, 'monthly_spend', userLevel).value ?? '🔒';
  const sfRefund   = (v: string) => sf(v, 'refund',        userLevel).value ?? '🔒';
  const sfShipping = (v: string) => sf(v, 'shipping',      userLevel).value ?? '🔒';
  const sfRefRate  = (v: string) => sf(v, 'refund_rate',   userLevel).value ?? '🔒';
  const [activeTab, setActiveTab] = useState<'funnel' | 'profit' | 'sku' | 'ltv'>('funnel');
  const [dateRange, setDateRange] = useState<'7' | '30' | '90'>('30');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'excel' | 'csv'>('pdf');
  const [exportDateRange, setExportDateRange] = useState<'7' | '30' | '90'>('30');
  const [profitPage, setProfitPage] = useState(1);
  const [skuPage, setSkuPage] = useState(1);
  const [profitPageSize, setProfitPageSize] = useState(5);
  const [skuPageSize, setSkuPageSize] = useState(5);
  const [selectedProfitRow, setSelectedProfitRow] = useState<ProfitRow | null>(null);
  const [selectedSkuRow, setSelectedSkuRow] = useState<SkuRow | null>(null);

  const profitData: ProfitRow[] = [
    { site: 'US', revenue: '$98,200', adSpend: '$22,300', refund: '$9,820', refundRate: '10.0%', shipping: '$5,892', profit: '$60,188', margin: '61.3%', trend: 'up', trendVal: '+15.2%' },
    { site: 'DE', revenue: '$42,600', adSpend: '$9,800', refund: '$5,112', refundRate: '12.0%', shipping: '$2,982', profit: '$24,706', margin: '58.0%', trend: 'up', trendVal: '+8.7%' },
    { site: 'UK', revenue: '$38,400', adSpend: '$8,640', refund: '$3,456', refundRate: '9.0%', shipping: '$2,304', profit: '$24,000', margin: '62.5%', trend: 'up', trendVal: '+22.1%' },
    { site: 'FR', revenue: '$22,140', adSpend: '$5,320', refund: '$2,878', refundRate: '13.0%', shipping: '$1,549', profit: '$12,393', margin: '56.0%', trend: 'down', trendVal: '-3.2%' },
    { site: 'JP', revenue: '$12,100', adSpend: '$1,832', refund: '$726', refundRate: '6.0%', shipping: '$1,089', profit: '$8,453', margin: '69.9%', trend: 'up', trendVal: '+31.5%' },
    { site: 'CA', revenue: '$35,800', adSpend: '$8,200', refund: '$3,580', refundRate: '10.0%', shipping: '$2,148', profit: '$21,872', margin: '61.1%', trend: 'up', trendVal: '+12.3%' },
    { site: 'AU', revenue: '$28,400', adSpend: '$6,800', refund: '$2,840', refundRate: '10.0%', shipping: '$1,704', profit: '$17,056', margin: '60.1%', trend: 'down', trendVal: '-1.5%' },
    { site: 'IT', revenue: '$18,600', adSpend: '$4,500', refund: '$2,232', refundRate: '12.0%', shipping: '$1,116', profit: '$10,752', margin: '57.8%', trend: 'up', trendVal: '+5.2%' },
    { site: 'ES', revenue: '$15,200', adSpend: '$3,800', refund: '$1,824', refundRate: '12.0%', shipping: '$912', profit: '$8,664', margin: '57.0%', trend: 'up', trendVal: '+3.8%' },
    { site: 'NL', revenue: '$22,800', adSpend: '$5,200', refund: '$2,280', refundRate: '10.0%', shipping: '$1,368', profit: '$13,952', margin: '61.2%', trend: 'up', trendVal: '+9.1%' },
    { site: 'SE', revenue: '$14,500', adSpend: '$3,500', refund: '$1,450', refundRate: '10.0%', shipping: '$870', profit: '$8,680', margin: '59.9%', trend: 'down', trendVal: '-2.1%' },
    { site: 'PL', revenue: '$11,200', adSpend: '$2,800', refund: '$1,344', refundRate: '12.0%', shipping: '$672', profit: '$6,384', margin: '57.0%', trend: 'up', trendVal: '+4.5%' },
  ];

  const skuData: SkuRow[] = [
    { sku: 'ZL-1207', name: '钛合金半框', spend: '$12,400', orders: 1820, revenue: '$58,240', roas: '4.7x', profit: '$28,120', stock: '充足 (2,400)', suggestion: '加大投放', suggestionColor: 'green' },
    { sku: 'ZL-3301', name: '无框钻切', spend: '$8,600', orders: 1240, revenue: '$39,680', roas: '4.6x', profit: '$18,900', stock: '充足 (1,800)', suggestion: '加大投放', suggestionColor: 'green' },
    { sku: 'ZL-2045', name: '猫眼框', spend: '$5,200', orders: 680, revenue: '$17,680', roas: '3.4x', profit: '$7,240', stock: '偏低 (320)', suggestion: '补货后扩量', suggestionColor: 'amber' },
    { sku: 'ZL-0892', name: '蓝光防护镜', spend: '$6,800', orders: 520, revenue: '$14,560', roas: '2.1x', profit: '$2,120', stock: '缺货 (0)', suggestion: '已暂停', suggestionColor: 'red' },
    { sku: 'ZL-4520', name: '渐进多焦', spend: '$3,400', orders: 180, revenue: '$5,400', roas: '1.6x', profit: '-$680', stock: '充足 (900)', suggestion: '优化素材', suggestionColor: 'red' },
    { sku: 'ZL-5612', name: '运动款偏光', spend: '$7,200', orders: 890, revenue: '$28,450', roas: '3.9x', profit: '$12,890', stock: '充足 (1,500)', suggestion: '加大投放', suggestionColor: 'green' },
    { sku: 'ZL-7834', name: '复古圆框', spend: '$4,800', orders: 620, revenue: '$18,600', roas: '3.8x', profit: '$8,420', stock: '充足 (1,200)', suggestion: '加大投放', suggestionColor: 'green' },
    { sku: 'ZL-9021', name: '儿童防蓝光', spend: '$3,600', orders: 450, revenue: '$12,150', roas: '3.3x', profit: '$5,280', stock: '偏低 (180)', suggestion: '补货后扩量', suggestionColor: 'amber' },
    { sku: 'ZL-3456', name: '商务半框', spend: '$5,500', orders: 380, revenue: '$13,300', roas: '2.4x', profit: '$3,120', stock: '充足 (800)', suggestion: '观察', suggestionColor: 'amber' },
    { sku: 'ZL-6789', name: '时尚大框', spend: '$6,200', orders: 520, revenue: '$15,600', roas: '2.5x', profit: '$4,280', stock: '充足 (950)', suggestion: '观察', suggestionColor: 'amber' },
    { sku: 'ZL-1234', name: '轻量钛架', spend: '$8,900', orders: 720, revenue: '$28,800', roas: '3.2x', profit: '$11,520', stock: '充足 (1,100)', suggestion: '加大投放', suggestionColor: 'green' },
    { sku: 'ZL-5678', name: '折叠便携款', spend: '$2,800', orders: 210, revenue: '$6,300', roas: '2.2x', profit: '$1,680', stock: '偏低 (120)', suggestion: '补货后扩量', suggestionColor: 'amber' },
    { sku: 'ZL-9012', name: '智能变色镜', spend: '$4,200', orders: 280, revenue: '$11,200', roas: '2.6x', profit: '$3,640', stock: '充足 (600)', suggestion: '观察', suggestionColor: 'amber' },
    { sku: 'ZL-3457', name: '驾驶专用', spend: '$3,100', orders: 195, revenue: '$7,800', roas: '2.5x', profit: '$2,340', stock: '充足 (450)', suggestion: '观察', suggestionColor: 'amber' },
    { sku: 'ZL-7890', name: '老花镜', spend: '$1,800', orders: 120, revenue: '$3,600', roas: '2.0x', profit: '$720', stock: '充足 (300)', suggestion: '优化素材', suggestionColor: 'amber' },
    { sku: 'ZL-2468', name: '3D电影镜', spend: '$900', orders: 60, revenue: '$1,200', roas: '1.3x', profit: '-$180', stock: '缺货 (0)', suggestion: '已暂停', suggestionColor: 'red' },
    { sku: 'ZL-1357', name: 'VR适配镜', spend: '$1,200', orders: 45, revenue: '$1,350', roas: '1.1x', profit: '-$420', stock: '充足 (200)', suggestion: '优化素材', suggestionColor: 'red' },
    { sku: 'ZL-8642', name: '夜视镜', spend: '$2,400', orders: 150, revenue: '$5,250', roas: '2.1x', profit: '$1,575', stock: '偏低 (80)', suggestion: '补货后扩量', suggestionColor: 'amber' },
    { sku: 'ZL-9753', name: '泳镜', spend: '$1,500', orders: 95, revenue: '$3,325', roas: '2.2x', profit: '$1,015', stock: '充足 (250)', suggestion: '观察', suggestionColor: 'amber' },
    { sku: 'ZL-5319', name: '滑雪镜', spend: '$3,800', orders: 220, revenue: '$9,900', roas: '2.6x', profit: '$3,300', stock: '充足 (400)', suggestion: '观察', suggestionColor: 'amber' },
    { sku: 'ZL-6420', name: '电竞护目镜', spend: '$5,600', orders: 380, revenue: '$17,100', roas: '3.0x', profit: '$6,840', stock: '充足 (700)', suggestion: '加大投放', suggestionColor: 'green' },
    { sku: 'ZL-7531', name: '防辐射镜', spend: '$2,200', orders: 140, revenue: '$4,900', roas: '2.2x', profit: '$1,470', stock: '充足 (350)', suggestion: '观察', suggestionColor: 'amber' },
    { sku: 'ZL-8643', name: '偏光太阳镜', spend: '$9,500', orders: 850, revenue: '$38,000', roas: '4.0x', profit: '$18,050', stock: '充足 (1,800)', suggestion: '加大投放', suggestionColor: 'green' },
    { sku: 'ZL-9754', name: '渐进老花', spend: '$4,100', orders: 260, revenue: '$10,400', roas: '2.5x', profit: '$3,380', stock: '偏低 (150)', suggestion: '补货后扩量', suggestionColor: 'amber' },
    { sku: 'ZL-0865', name: '防雾镜', spend: '$1,600', orders: 100, revenue: '$3,500', roas: '2.1x', profit: '$1,050', stock: '充足 (280)', suggestion: '观察', suggestionColor: 'amber' },
    { sku: 'ZL-1976', name: '变色偏光', spend: '$3,300', orders: 200, revenue: '$8,000', roas: '2.4x', profit: '$2,400', stock: '充足 (420)', suggestion: '观察', suggestionColor: 'amber' },
    { sku: 'ZL-2087', name: '磁吸套镜', spend: '$6,800', orders: 480, revenue: '$21,600', roas: '3.1x', profit: '$8,640', stock: '充足 (900)', suggestion: '加大投放', suggestionColor: 'green' },
  ];

  const ltvData = [
    { channel: 'Google', firstBuyers: 3420, rate30: '12.8%', rate90: '28.4%', interval: '42 天', aov: '$62', ltv: '$192' },
    { channel: 'Facebook', firstBuyers: 5680, rate30: '10.2%', rate90: '22.6%', interval: '38 天', aov: '$55', ltv: '$168' },
    { channel: 'TikTok', firstBuyers: 3750, rate30: '7.5%', rate90: '16.8%', interval: '52 天', aov: '$48', ltv: '$128' },
  ];

  const profitTotalPages = Math.ceil(profitData.length / profitPageSize);
  const skuTotalPages = Math.ceil(skuData.length / skuPageSize);

  const paginatedProfitData = profitData.slice((profitPage - 1) * profitPageSize, profitPage * profitPageSize);
  const paginatedSkuData = skuData.slice((skuPage - 1) * skuPageSize, skuPage * skuPageSize);

  const handleExport = () => {
    setShowExportModal(false);
    showAlert(`归因报告已导出为 ${exportFormat.toUpperCase()} 格式，时间范围：近 ${exportDateRange} 天`, { type: 'success' });
  };

  return (
    <div className="page-section" style={{ display: 'block' }}>
      {isMobile ? (
        <div className="toolbar-mobile">
          <div className="toolbar-mobile-row tabs-row">
            <div className="page-tabs">
              <div className={`page-tab ${activeTab === 'funnel' ? 'active' : ''}`} onClick={() => setActiveTab('funnel')}>全链路归因</div>
              <div className={`page-tab ${activeTab === 'profit' ? 'active' : ''}`} onClick={() => setActiveTab('profit')}>净利润分析</div>
              <div className={`page-tab ${activeTab === 'sku' ? 'active' : ''}`} onClick={() => setActiveTab('sku')}>SKU 分析</div>
              <div className={`page-tab ${activeTab === 'ltv' ? 'active' : ''}`} onClick={() => setActiveTab('ltv')}>LTV 洞察</div>
            </div>
          </div>
          <div className="toolbar-mobile-row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <MultiSelect single options={[{value:'7',label:'近 7 天'},{value:'30',label:'近 30 天'},{value:'90',label:'近 90 天'}]} value={[dateRange]} onChange={(vals) => setDateRange((vals[0] ?? '30') as '7'|'30'|'90')} style={{ width: '100%' }} />
            </div>
            <div style={{ flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowExportModal(true)}>导出报告</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="toolbar">
          <div className="page-tabs">
            <div className={`page-tab ${activeTab === 'funnel' ? 'active' : ''}`} onClick={() => setActiveTab('funnel')}>全链路归因</div>
            <div className={`page-tab ${activeTab === 'profit' ? 'active' : ''}`} onClick={() => setActiveTab('profit')}>净利润分析</div>
            <div className={`page-tab ${activeTab === 'sku' ? 'active' : ''}`} onClick={() => setActiveTab('sku')}>SKU 分析</div>
            <div className={`page-tab ${activeTab === 'ltv' ? 'active' : ''}`} onClick={() => setActiveTab('ltv')}>LTV 洞察</div>
          </div>
          <div className="divider-v"></div>
          <MultiSelect single options={[{value:'7',label:'近 7 天'},{value:'30',label:'近 30 天'},{value:'90',label:'近 90 天'}]} value={[dateRange]} onChange={(vals) => setDateRange((vals[0] ?? '30') as '7'|'30'|'90')} />
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn btn-outline btn-sm" onClick={() => setShowExportModal(true)}>导出归因报告</button>
          </div>
        </div>
      )}

      {/* TAB: 全链路归因 */}
      {activeTab === 'funnel' && (
        <>
          <div className="card mb-24">
            <div className="card-header">
              <div>
                <div className="card-title">全链路归因漏斗</div>
                <div className="card-subtitle">广告点击 → 加购 → 下单 → 支付 → 签收 (后端 Order ID 匹配)</div>
              </div>
            </div>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' as unknown as undefined }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '20px 0', minWidth: 480 }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ background: 'linear-gradient(180deg,rgba(18,16,184,.15),rgba(18,16,184,.05))', borderRadius: 'var(--radius)', padding: 20, width: '100%' }}>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>842K</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>广告点击</div>
                </div>
              </div>
              <div style={{ color: 'var(--text3)', fontSize: 18 }}>→</div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ background: 'linear-gradient(180deg,rgba(13,148,136,.12),rgba(13,148,136,.03))', borderRadius: 'var(--radius)', padding: 20, width: '90%', margin: '0 auto' }}>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>68.4K</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>加购</div>
                  <div style={{ fontSize: 10, color: 'var(--cyan)', marginTop: 2 }}>8.1%</div>
                </div>
              </div>
              <div style={{ color: 'var(--text3)', fontSize: 18 }}>→</div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ background: 'linear-gradient(180deg,rgba(139,92,246,.12),rgba(139,92,246,.03))', borderRadius: 'var(--radius)', padding: 20, width: '75%', margin: '0 auto' }}>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>12.8K</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>下单</div>
                  <div style={{ fontSize: 10, color: 'var(--violet)', marginTop: 2 }}>18.7%</div>
                </div>
              </div>
              <div style={{ color: 'var(--text3)', fontSize: 18 }}>→</div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ background: 'linear-gradient(180deg,rgba(236,72,153,.12),rgba(236,72,153,.03))', borderRadius: 'var(--radius)', padding: 20, width: '60%', margin: '0 auto' }}>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>11.2K</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>支付成功</div>
                  <div style={{ fontSize: 10, color: 'var(--pink)', marginTop: 2 }}>87.5%</div>
                </div>
              </div>
              <div style={{ color: 'var(--text3)', fontSize: 18 }}>→</div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ background: 'linear-gradient(180deg,rgba(16,185,129,.12),rgba(16,185,129,.03))', borderRadius: 'var(--radius)', padding: 20, width: '50%', margin: '0 auto' }}>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>9.8K</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>签收确认</div>
                  <div style={{ fontSize: 10, color: 'var(--green)', marginTop: 2 }}>87.5%</div>
                </div>
              </div>
            </div>
            </div>{/* end overflow wrapper */}
          </div>

          <div className="grid-23">
            <div className="card">
              <div className="card-header"><div className="card-title">净利润归因 (按渠道)</div></div>
              <div className="table-wrap">
                <table style={{ minWidth: 480 }}>
                  <thead>
                    <tr><th style={{ whiteSpace: 'nowrap' }}>渠道</th><th style={{ whiteSpace: 'nowrap' }}>流水</th><th style={{ whiteSpace: 'nowrap' }}>广告费</th><th style={{ whiteSpace: 'nowrap' }}>退货</th><th style={{ whiteSpace: 'nowrap' }}>运费</th><th style={{ whiteSpace: 'nowrap' }}>净利润</th><th style={{ whiteSpace: 'nowrap' }}>净利率</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><span className="ch-tag fb">Facebook</span></td>
                      <td>{sfRevenue('$88,420')}</td>
                      <td style={{ color: 'var(--red)' }}>{sfSpend('-$20,115')}</td>
                      <td style={{ color: 'var(--red)' }}>{sfRefund('-$8,842')}</td>
                      <td style={{ color: 'var(--red)' }}>{sfShipping('-$5,305')}</td>
                      <td style={{ fontWeight: 700, color: 'var(--green)' }}>{sfProfit('$54,158')}</td>
                      <td style={{ fontWeight: 700 }}>{sfMargin('61.2%')}</td>
                    </tr>
                    <tr>
                      <td><span className="ch-tag tt">TikTok</span></td>
                      <td>{sfRevenue('$72,680')}</td>
                      <td style={{ color: 'var(--red)' }}>{sfSpend('-$16,762')}</td>
                      <td style={{ color: 'var(--red)' }}>{sfRefund('-$7,995')}</td>
                      <td style={{ color: 'var(--red)' }}>{sfShipping('-$4,361')}</td>
                      <td style={{ fontWeight: 700, color: 'var(--green)' }}>{sfProfit('$43,562')}</td>
                      <td style={{ fontWeight: 700 }}>{sfMargin('59.9%')}</td>
                    </tr>
                    <tr>
                      <td><span className="ch-tag gg">Google</span></td>
                      <td>{sfRevenue('$52,340')}</td>
                      <td style={{ color: 'var(--red)' }}>{sfSpend('-$11,015')}</td>
                      <td style={{ color: 'var(--red)' }}>{sfRefund('-$4,187')}</td>
                      <td style={{ color: 'var(--red)' }}>{sfShipping('-$3,140')}</td>
                      <td style={{ fontWeight: 700, color: 'var(--green)' }}>{sfProfit('$33,998')}</td>
                      <td style={{ fontWeight: 700 }}>{sfMargin('65.0%')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><div className="card-title">LTV 信号回传状态</div></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12 }}>高 LTV 客户识别率</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)' }}>82.4%</span>
                  </div>
                  <div className="progress"><div className="progress-fill" style={{ width: '82.4%', background: 'var(--green)' }}></div></div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12 }}>sGTM 回传成功率</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)' }}>96.8%</span>
                  </div>
                  <div className="progress"><div className="progress-fill" style={{ width: '96.8%', background: 'var(--cyan)' }}></div></div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12 }}>平台受众模型匹配度</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--amber)' }}>71.3%</span>
                  </div>
                  <div className="progress"><div className="progress-fill" style={{ width: '71.3%', background: 'var(--amber)' }}></div></div>
                </div>
                <div style={{ padding: 12, background: 'var(--green-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(16,185,129,.1)', marginTop: 4 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                    <strong style={{ color: 'var(--green)' }}>Feedback Loop 运行正常</strong> — 过去 7 天回传 4,218 条高 LTV 信号
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* TAB: 净利润分析 */}
      {activeTab === 'profit' && (
        <>
          <div className="card mb-24">
            <div className="card-title" style={{ marginBottom: 16 }}>净利润趋势 (近 {dateRange} 天)</div>
            <div className="chart-bars" style={{ height: 160, padding: 0 }}>
              <div className="chart-bar" style={{ height: '45%', background: 'linear-gradient(var(--green),rgba(16,185,129,.3))', flex: 1 }}><div className="chart-bar-label">2/20</div></div>
              <div className="chart-bar" style={{ height: '52%', background: 'linear-gradient(var(--green),rgba(16,185,129,.3))', flex: 1 }}><div className="chart-bar-label">2/25</div></div>
              <div className="chart-bar" style={{ height: '48%', background: 'linear-gradient(var(--green),rgba(16,185,129,.3))', flex: 1 }}><div className="chart-bar-label">3/1</div></div>
              <div className="chart-bar" style={{ height: '60%', background: 'linear-gradient(var(--green),rgba(16,185,129,.3))', flex: 1 }}><div className="chart-bar-label">3/5</div></div>
              <div className="chart-bar" style={{ height: '55%', background: 'linear-gradient(var(--green),rgba(16,185,129,.3))', flex: 1 }}><div className="chart-bar-label">3/10</div></div>
              <div className="chart-bar" style={{ height: '72%', background: 'linear-gradient(var(--green),rgba(16,185,129,.3))', flex: 1 }}><div className="chart-bar-label">3/15</div></div>
              <div className="chart-bar" style={{ height: '85%', background: 'linear-gradient(var(--green),rgba(16,185,129,.3))', flex: 1 }}><div className="chart-bar-label">3/20</div></div>
            </div>
          </div>

          <div className="grid-3 mb-24">
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>总流水</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{sfRevenue('$213,440')}</div>
            </div>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>总成本 (广告+退货+运费)</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--red)' }}>{sfMonthly('$81,722')}</div>
            </div>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>净利润</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--green)' }}>{sfProfit('$131,718')}</div>
              <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 4 }}>净利率 {sfMargin('61.7%')}</div>
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>利润拆解 (按国家站点)</div>
            <div className="table-wrap">
              <table style={{ minWidth: 640 }}>
                <thead>
                  <tr><th style={{ whiteSpace: 'nowrap' }}>站点</th><th style={{ whiteSpace: 'nowrap' }}>流水</th><th style={{ whiteSpace: 'nowrap' }}>广告费</th><th style={{ whiteSpace: 'nowrap' }}>退货额</th><th style={{ whiteSpace: 'nowrap' }}>退货率</th><th style={{ whiteSpace: 'nowrap' }}>运费</th><th style={{ whiteSpace: 'nowrap' }}>净利润</th><th style={{ whiteSpace: 'nowrap' }}>净利率</th><th style={{ whiteSpace: 'nowrap' }}>趋势</th></tr>
                </thead>
                <tbody>
                  {paginatedProfitData.map((row, idx) => (
                    <tr key={idx} onClick={() => setSelectedProfitRow(row)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontWeight: 600 }}>{row.site}</td>
                      <td>{sfRevenue(row.revenue)}</td>
                      <td>{sfSpend(row.adSpend)}</td>
                      <td>{sfRefund(row.refund)}</td>
                      <td>{sfRefRate(row.refundRate)}</td>
                      <td>{sfShipping(row.shipping)}</td>
                      <td style={{ color: 'var(--green)', fontWeight: 700 }}>{sfProfit(row.profit)}</td>
                      <td>{sfMargin(row.margin)}</td>
                      <td><span className={`metric-change ${row.trend}`}>{row.trendVal}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={profitPage}
              totalPages={profitTotalPages}
              onChange={setProfitPage}
              total={`共 ${profitData.length} 个站点`}
              pageSize={profitPageSize}
              pageSizeOptions={[5, 10, 20, 50]}
              onPageSizeChange={(nextPageSize) => { setProfitPageSize(nextPageSize); setProfitPage(1); }}
            />
          </div>
        </>
      )}

      {/* TAB: SKU 分析 */}
      {activeTab === 'sku' && (
        <>
          <div className="grid-3 mb-24">
            <div className="card" style={{ textAlign: 'center', borderTop: '2px solid var(--green)' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>爆款 SKU</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--green)' }}>8</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>ROAS &gt; 3.5x</div>
            </div>
            <div className="card" style={{ textAlign: 'center', borderTop: '2px solid var(--amber)' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>观察期 SKU</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--amber)' }}>15</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>ROAS 2.0-3.5x</div>
            </div>
            <div className="card" style={{ textAlign: 'center', borderTop: '2px solid var(--red)' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>亏损 SKU</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--red)' }}>4</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>ROAS &lt; 2.0x</div>
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>SKU 投放效果排行</div>
            <div className="table-wrap">
              <table style={{ minWidth: 680 }}>
                <thead>
                  <tr><th style={{ whiteSpace: 'nowrap' }}>SKU</th><th style={{ whiteSpace: 'nowrap' }}>产品名称</th><th style={{ whiteSpace: 'nowrap' }}>花费</th><th style={{ whiteSpace: 'nowrap' }}>订单数</th><th style={{ whiteSpace: 'nowrap' }}>收入</th><th style={{ whiteSpace: 'nowrap' }}>ROAS</th><th style={{ whiteSpace: 'nowrap' }}>净利润</th><th style={{ whiteSpace: 'nowrap' }}>库存</th><th style={{ whiteSpace: 'nowrap' }}>建议</th></tr>
                </thead>
                <tbody>
                  {paginatedSkuData.map((row, idx) => (
                    <tr key={idx} onClick={() => setSelectedSkuRow(row)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontWeight: 600 }}>{row.sku}</td>
                      <td>{row.name}</td>
                      <td>{sfSpend(row.spend)}</td>
                      <td>{row.orders}</td>
                      <td>{sfRevenue(row.revenue)}</td>
                      <td style={{ color: row.roas.startsWith('4') ? 'var(--green)' : row.roas.startsWith('3') ? 'var(--green)' : row.roas.startsWith('2') ? 'var(--amber)' : 'var(--red)', fontWeight: 700 }}>{row.roas}</td>
                      <td style={{ color: row.profit.startsWith('-') ? 'var(--red)' : 'var(--green)', fontWeight: 700 }}>{sfProfit(row.profit)}</td>
                      <td style={{ color: row.stock.includes('充足') ? 'var(--green)' : row.stock.includes('偏低') ? 'var(--amber)' : 'var(--red)' }}>{row.stock}</td>
                      <td><span style={{ color: `var(--${row.suggestionColor})`, fontSize: 11, fontWeight: 600 }}>{row.suggestion}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={skuPage}
              totalPages={skuTotalPages}
              onChange={setSkuPage}
              total={`共 ${skuData.length} 个 SKU`}
              pageSize={skuPageSize}
              pageSizeOptions={[5, 10, 20, 50]}
              onPageSizeChange={(nextPageSize) => { setSkuPageSize(nextPageSize); setSkuPage(1); }}
            />
          </div>
        </>
      )}

      {/* TAB: LTV 洞察 */}
      {activeTab === 'ltv' && (
        <>
          <div className="grid-2 mb-24">
            <div className="card">
              <div className="card-title" style={{ marginBottom: 16 }}>客户生命周期价值分层</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>高价值 (LTV &gt; $200)</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cyan)' }}>18.2% · 2,340 人</span>
                  </div>
                  <div className="progress"><div className="progress-fill" style={{ width: '18.2%', background: 'var(--cyan)' }}></div></div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>中等 (LTV $80-200)</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)' }}>45.6% · 5,860 人</span>
                  </div>
                  <div className="progress"><div className="progress-fill" style={{ width: '45.6%', background: 'var(--green)' }}></div></div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>低价值 (LTV &lt; $80)</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--amber)' }}>36.2% · 4,650 人</span>
                  </div>
                  <div className="progress"><div className="progress-fill" style={{ width: '36.2%', background: 'var(--amber)' }}></div></div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-title" style={{ marginBottom: 16 }}>渠道获客 LTV 对比</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className="ch-tag fb" style={{ minWidth: 70, textAlign: 'center' }}>Facebook</span>
                  <div style={{ flex: 1 }}>
                    <div className="progress" style={{ height: 24, borderRadius: 4 }}>
                      <div className="progress-fill" style={{ width: '72%', background: 'var(--fb-color)', borderRadius: 4, display: 'flex', alignItems: 'center', paddingLeft: 8, fontSize: 11, fontWeight: 700, color: '#fff' }}>$168 平均 LTV</div>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className="ch-tag gg" style={{ minWidth: 70, textAlign: 'center' }}>Google</span>
                  <div style={{ flex: 1 }}>
                    <div className="progress" style={{ height: 24, borderRadius: 4 }}>
                      <div className="progress-fill" style={{ width: '82%', background: 'var(--gg-color)', borderRadius: 4, display: 'flex', alignItems: 'center', paddingLeft: 8, fontSize: 11, fontWeight: 700, color: '#fff' }}>$192 平均 LTV</div>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className="ch-tag tt" style={{ minWidth: 70, textAlign: 'center' }}>TikTok</span>
                  <div style={{ flex: 1 }}>
                    <div className="progress" style={{ height: 24, borderRadius: 4 }}>
                      <div className="progress-fill" style={{ width: '55%', background: 'var(--tt-color)', borderRadius: 4, display: 'flex', alignItems: 'center', paddingLeft: 8, fontSize: 11, fontWeight: 700, color: '#000' }}>$128 平均 LTV</div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ padding: 12, background: 'var(--primary-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(18,16,184,.1)', marginTop: 16 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--primary-light)' }}>AI 洞察:</strong> Google 获客 LTV 最高但 CPA 偏高，建议对 Google 高 LTV 用户做 FB/TT 跨渠道再营销
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>复购率分析 (按首购渠道)</div>
            <div className="table-wrap">
              <table style={{ minWidth: 560 }}>
                <thead>
                  <tr><th style={{ whiteSpace: 'nowrap' }}>首购渠道</th><th style={{ whiteSpace: 'nowrap' }}>首购客户数</th><th style={{ whiteSpace: 'nowrap' }}>30 天复购率</th><th style={{ whiteSpace: 'nowrap' }}>90 天复购率</th><th style={{ whiteSpace: 'nowrap' }}>平均复购间隔</th><th style={{ whiteSpace: 'nowrap' }}>复购 AOV</th><th style={{ whiteSpace: 'nowrap' }}>LTV 预估</th></tr>
                </thead>
                <tbody>
                  {ltvData.map((row, idx) => (
                    <tr key={idx}>
                      <td><span className={`ch-tag ${row.channel === 'Google' ? 'gg' : row.channel === 'Facebook' ? 'fb' : 'tt'}`}>{row.channel}</span></td>
                      <td>{row.firstBuyers.toLocaleString()}</td>
                      <td>{row.rate30}</td>
                      <td style={{ fontWeight: 700, color: row.rate90 === '28.4%' ? 'var(--green)' : 'var(--text2)' }}>{row.rate90}</td>
                      <td>{row.interval}</td>
                      <td>{row.aov}</td>
                      <td style={{ fontWeight: 700 }}>{row.ltv}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal-overlay open" onClick={() => setShowExportModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>导出归因报告</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowExportModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>导出格式</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className={`btn btn-sm ${exportFormat === 'pdf' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setExportFormat('pdf')}
                  >
                    PDF
                  </button>
                  <button
                    className={`btn btn-sm ${exportFormat === 'excel' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setExportFormat('excel')}
                  >
                    Excel
                  </button>
                  <button
                    className={`btn btn-sm ${exportFormat === 'csv' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setExportFormat('csv')}
                  >
                    CSV
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>时间范围</label>
                <MultiSelect single options={[{value:'7',label:'近 7 天'},{value:'30',label:'近 30 天'},{value:'90',label:'近 90 天'}]} value={[exportDateRange]} onChange={(vals) => setExportDateRange((vals[0] ?? '30') as '7'|'30'|'90')} style={{ width: '100%' }} />
              </div>
              <div style={{ padding: 12, background: 'var(--primary-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(18,16,184,.1)' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                  报告将包含：全链路归因漏斗、净利润分析、SKU 分析、LTV 洞察数据
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowExportModal(false)}>取消</button>
              <button className="btn btn-primary" onClick={handleExport}>确认导出</button>
            </div>
          </div>
        </div>
      )}

      {/* Profit Detail Side Panel */}
      {selectedProfitRow && (
        <div className="side-panel open">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>{selectedProfitRow.site} 站点详情</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelectedProfitRow(null)}>✕</button>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>总流水</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{sfRevenue(selectedProfitRow.revenue)}</div>
              </div>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>净利润</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--green)' }}>{sfProfit(selectedProfitRow.profit)}</div>
              </div>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>净利率</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{sfMargin(selectedProfitRow.margin)}</div>
              </div>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>环比趋势</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: selectedProfitRow.trend === 'up' ? 'var(--green)' : 'var(--red)' }}>{selectedProfitRow.trendVal}</div>
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, marginBottom: 12 }}>成本构成</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                    <span>广告费</span>
                    <span style={{ color: 'var(--red)' }}>{sfSpend(selectedProfitRow.adSpend)}</span>
                  </div>
                  <div className="progress"><div className="progress-fill" style={{ width: '60%', background: 'var(--red)' }}></div></div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                    <span>退货额 ({sfRefRate(selectedProfitRow.refundRate)})</span>
                    <span style={{ color: 'var(--amber)' }}>{sfRefund(selectedProfitRow.refund)}</span>
                  </div>
                  <div className="progress"><div className="progress-fill" style={{ width: '30%', background: 'var(--amber)' }}></div></div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                    <span>运费</span>
                    <span style={{ color: 'var(--cyan)' }}>{sfShipping(selectedProfitRow.shipping)}</span>
                  </div>
                  <div className="progress"><div className="progress-fill" style={{ width: '20%', background: 'var(--cyan)' }}></div></div>
                </div>
              </div>
            </div>
            <div style={{ padding: 12, background: 'var(--primary-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(18,16,184,.1)' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--primary-light)' }}>AI 建议：</strong>
                {selectedProfitRow.trend === 'up' ? '该站点表现良好，建议保持当前投放策略并适当扩量。' : '该站点利润下滑，建议检查广告素材和受众定位，优化成本结构。'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SKU Detail Side Panel */}
      {selectedSkuRow && (
        <div className="side-panel open">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>{selectedSkuRow.name} 详情</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelectedSkuRow(null)}>✕</button>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <span style={{ fontSize: 12, padding: '4px 12px', borderRadius: 4, fontWeight: 600, background: 'var(--primary-bg)', color: 'var(--primary-light)' }}>{selectedSkuRow.sku}</span>
              <span style={{ color: `var(--${selectedSkuRow.suggestionColor})`, fontSize: 12, fontWeight: 600 }}>{selectedSkuRow.suggestion}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>广告花费</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{sfSpend(selectedSkuRow.spend)}</div>
              </div>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>订单数</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{selectedSkuRow.orders}</div>
              </div>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>收入</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{sfRevenue(selectedSkuRow.revenue)}</div>
              </div>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>ROAS</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: selectedSkuRow.roas.startsWith('4') || selectedSkuRow.roas.startsWith('3') ? 'var(--green)' : selectedSkuRow.roas.startsWith('2') ? 'var(--amber)' : 'var(--red)' }}>{selectedSkuRow.roas}</div>
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, marginBottom: 12 }}>库存状态</h4>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: selectedSkuRow.stock.includes('充足') ? 'var(--green)' : selectedSkuRow.stock.includes('偏低') ? 'var(--amber)' : 'var(--red)' }}>
                  {selectedSkuRow.stock}
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, marginBottom: 12 }}>净利润</h4>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: selectedSkuRow.profit.startsWith('-') ? 'var(--red)' : 'var(--green)' }}>
                  {sfProfit(selectedSkuRow.profit)}
                </div>
              </div>
            </div>
            <div style={{ padding: 12, background: 'var(--primary-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(18,16,184,.1)' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--primary-light)' }}>AI 建议：</strong>
                {selectedSkuRow.suggestion === '加大投放' ? '该产品 ROAS 表现优异，库存充足，建议加大投放力度获取更多订单。' :
                 selectedSkuRow.suggestion === '补货后扩量' ? '产品转化良好但库存偏低，建议优先补货后再扩大投放。' :
                 selectedSkuRow.suggestion === '已暂停' ? '产品缺货或 ROAS 过低，已自动暂停投放，请检查库存或优化素材。' :
                 '建议优化广告素材或调整受众定位以提升转化率。'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
